"use client";

import { useExperimentVariant } from "@sa-nu/experiments/experiment-context";
import {
  genPlainDateList,
  isNationalHoliday,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import type { PlainDate } from "@sa-nu/shared-utils/date";
import clsx from "clsx";

import type { AccommodationReservationProviderInput } from "../../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { ChevronRightIcon } from "../../../dx-icons";
import { useSiteDetailDrawer } from "../../../site-detail/site-detail-drawer-provider";
import { useAvailabilityStripContext } from "../../_context/availability-strip-context";
import { useAvailabilityStripRangeSize } from "../../_hooks/use-availability-strip-range-size";
import {
  useSiteAvailability,
  type SiteAvailabilityNight,
} from "../../_hooks/use-site-availability";

import { AvailabilityStripDayCell } from "./_components/availability-strip-day-cell";

// PlainDate.dayOfWeek は ISO8601 (1=月 〜 7=日) を返すため、この順で並べる。
const WEEKDAY_LABELS = ["月", "火", "水", "木", "金", "土", "日"] as const;

function isHolidayDate(day: PlainDate): boolean {
  return day.dayOfWeek === 6 || day.dayOfWeek === 7 || isNationalHoliday(day);
}

function formatWeekRangeLabel(rangeStart: PlainDate, rangeEnd: PlainDate) {
  const format = (date: PlainDate) =>
    `${date.month}/${date.day}(${WEEKDAY_LABELS[date.dayOfWeek - 1]})`;
  return `${format(rangeStart)} 〜 ${format(rangeEnd)}`;
}

type AvailabilityStripProps = {
  siteId: string;
  siteSlug: string;
  provider: AccommodationReservationProviderInput;
  facilitySlugs: string[];
  evChargerAvailable: boolean;
  architectureTypes: string[];
  largeOnly: boolean;
};

/**
 * 拠点一覧（SiteCard）用の拠点別カレンダー。表示週（rangeStart）は `AvailabilityStripProvider` で全拠点カード間共有される。
 */
export function AvailabilityStrip(props: AvailabilityStripProps) {
  const variant = useExperimentVariant("site-level-calendar");
  return variant === "treatment" ? (
    <TreatmentAvailabilityStrip {...props} />
  ) : (
    <ControlAvailabilityStrip />
  );
}

function ControlAvailabilityStrip() {
  return null;
}

function TreatmentAvailabilityStrip({
  siteId,
  siteSlug,
  provider,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
}: AvailabilityStripProps) {
  const { rangeStart, debouncedRangeStart, shiftRange } =
    useAvailabilityStripContext();
  const { open } = useSiteDetailDrawer();
  const { rangeSize, isRangeSizeResolved } = useAvailabilityStripRangeSize();
  const gtm = useGtm();

  // 週の見出し・セルグリッドの描画は即時値の rangeStart を使う。データフェッチだけ
  // debouncedRangeStart 経由にし、週送り連打時の SiteAvailabilityQuery 過剰発火を防ぐ。
  const { nights } = useSiteAvailability({
    siteId,
    debouncedRangeStart,
    rangeSize,
    isRangeSizeResolved,
    provider,
    facilitySlugs,
    evChargerAvailable,
    architectureTypes,
    largeOnly,
  });
  const nightByDate = new Map<string, SiteAvailabilityNight>(
    nights.map((n) => [n.night, n]),
  );

  const rangeEnd = rangeStart.add({ days: rangeSize - 1 });
  const days = genPlainDateList(rangeStart, rangeEnd);

  const handleSelect = (night: PlainDate) => {
    open({ siteSlug, selectedNight: night });
  };

  // shiftRange は新しい rangeStart を戻り値で返さないため、GTM 送信用に
  // computeAvailabilityStripRange と同じ式でここでも算出する。
  const handleShiftRange = (deltaNights: number) => {
    shiftRange(deltaNights);
    const newRangeStart = rangeStart.add({ days: deltaNights });
    gtm.sendEvent("rt_rsv_calendar_week_shifted", {
      range_start: toPlainDateString(newRangeStart),
      range_end: toPlainDateString(newRangeStart.add({ days: rangeSize - 1 })),
    });
  };

  return (
    <div className="px-[16px] lg:px-0">
      <div className="flex flex-col gap-[8px]">
        <div className="flex w-full items-center gap-[8px]">
          <button
            data-analytics-id="room_type_browse_clicked_previous_week"
            type="button"
            data-testid="week-nav-prev"
            aria-label="前の週"
            onClick={() => handleShiftRange(-rangeSize)}
            className="text-dx-text-default hover:bg-dx-bg-base-hover flex size-[32px] shrink-0 items-center justify-center rounded-full transition-colors duration-150"
          >
            <ChevronRightIcon className="size-[16px] rotate-180" />
          </button>

          <p className="font-notojp text-dx-text-default flex-1 text-center text-[14px] leading-[24px] font-bold">
            {formatWeekRangeLabel(rangeStart, rangeEnd)}
          </p>

          <button
            data-analytics-id="room_type_browse_clicked_next_week"
            type="button"
            data-testid="week-nav-next"
            aria-label="次の週"
            onClick={() => handleShiftRange(rangeSize)}
            className="text-dx-text-default hover:bg-dx-bg-base-hover flex size-[32px] shrink-0 items-center justify-center rounded-full transition-colors duration-150"
          >
            <ChevronRightIcon className="size-[16px]" />
          </button>
        </div>

        <div className="flex w-full gap-[2px]">
          {days.map((day) => {
            const dateKey = toPlainDateString(day);
            const isHoliday = isHolidayDate(day);

            return (
              <div
                key={dateKey}
                className="flex flex-1 flex-col items-center gap-[3px]"
              >
                <p className="font-notojp text-dx-text-sub text-[12px] leading-none">
                  {WEEKDAY_LABELS[day.dayOfWeek - 1]}
                </p>
                <p
                  className={clsx(
                    "font-notojp text-[10px] leading-none",
                    isHoliday ? "text-dx-text-holiday" : "text-dx-text-sub",
                  )}
                >
                  {day.day}
                </p>
                <AvailabilityStripDayCell
                  info={nightByDate.get(dateKey)}
                  onSelect={() => handleSelect(day)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
